import { jwtDecode } from 'jwt-decode';
import { SessionContext } from '../types/sessionContext';
import {
  browserSupportsWebAuthn,
  RegistrationResponseJSON,
  startAuthentication,
  startRegistration,
  WebAuthnAbortService,
} from '@simplewebauthn/browser';

const apiBase = import.meta.env.ECOS_API_BASE;
const HAS_PASSKEY_AUTH_KEY = 'has-passkey-auth';

export const storeAdminToken = (token: string): void => {
  localStorage.setItem('admin-token', token);
};

export const storeToken = (token: string): void => {
  localStorage.setItem('auth-token', token);
};

export const hasToken = (): boolean => {
  return getToken() !== null;
};

export const getAdminToken = (): string | null => {
  return localStorage.getItem('admin-token');
};

export const getToken = (): string | null => {
  return localStorage.getItem('auth-token');
};

export const getTokenData = (): SessionContext => {
  const token = getToken();
  if (!token) {
    throw new Error('No token data available');
  }
  return jwtDecode(token);
};

const AUTH_PATHS = ['/login', '/logout', '/forgot-password', '/password-reset', '/token'];

const getLoginRedirectPath = () => {
  const redirect = new URLSearchParams(window.location.search).get('redirect');

  if (!redirect || !redirect.startsWith('/') || redirect.startsWith('//')) {
    return null;
  }

  const redirectPath = redirect.split(/[?#]/)[0];

  if (
    AUTH_PATHS.some(
      (authPath) =>
        redirectPath === authPath || redirectPath.startsWith(`${authPath}/`)
    )
  ) {
    return null;
  }

  return redirect;
};

type TokenPayload = {
  token: string;
  shouldPromptForPasskeyRegistration?: boolean;
};

const isTokenPayload = (data: any): data is TokenPayload => {
  return 'token' in data;
};

type ErrorFormat = {
  errors: { message: string }[];
};
const isErrorFormat = (data: any): data is ErrorFormat => {
  return (
    'errors' in data && data.errors instanceof Array && data.errors.length > 0
  );
};

const getPasskeyAuthStorageKey = (token: string): string => {
  const sessionContext = jwtDecode<SessionContext>(token);
  return `${HAS_PASSKEY_AUTH_KEY}:${apiBase}:${sessionContext.profile.id}`;
};

const getHasPasskeyAuth = (token: string): boolean => {
  return JSON.parse(
    localStorage.getItem(getPasskeyAuthStorageKey(token)) || 'false'
  );
};

const setHasPasskeyAuth = (token: string): void => {
  localStorage.setItem(getPasskeyAuthStorageKey(token), 'true');
};

export const goToHomePage = () => {
  const redirectPath = getLoginRedirectPath();

  if (redirectPath) {
    window.location.replace(redirectPath);
    return;
  }

  const sessionContext = getTokenData();

  if (!sessionContext.modes) {
    console.warn('No modes found in token data', sessionContext);
    logout();
    return;
  }

  if (sessionContext.modes.includes('ADMIN')) {
    window.location.replace('/admin/users');
  } else if (sessionContext.modes.includes('WATER_SUPPLIER')) {
    window.location.replace('/water-supplier/');
  } else if (sessionContext.modes.includes('COMPANY')) {
    window.location.replace('/company/jobs');
  } else if (
    sessionContext.modes.includes('INDIVIDUAL') &&
    window.location.pathname !== '/individual/jobs'
  ) {
    window.location.replace('/individual/jobs');
  }
};

type LoginHandlerOptions = {
  promptForPasskeyRegistration?: boolean;
  hasPasskeyAuth?: boolean;
};

const loginHandler = async (
  res: Response,
  options: LoginHandlerOptions = {}
) => {
  const data = await res.json();
  if (res.status >= 400) {
    if (data.message) {
      throw new Error(data.message);
    }
  }

  if (data && isTokenPayload(data)) {
    const existingToken = getToken();
    if (existingToken) {
      storeAdminToken(existingToken);
    }

    storeToken(data.token);

    if (options.hasPasskeyAuth) {
      setHasPasskeyAuth(data.token);
    }

    if (
      browserSupportsWebAuthn() &&
      (options.promptForPasskeyRegistration ?? true) &&
      !getHasPasskeyAuth(data.token)
    ) {
      try {
        await registerPasskey();
      } catch (err) {
        console.error(err);
      }
    }

    goToHomePage();
    return;
  }
  if (data && isErrorFormat(data)) {
    throw new Error(data.errors[0].message);
  }

  console.error('Login response', data);
  throw new Error('Invalid login response');
};

export const login = (email: string, password: string): Promise<void> => {
  return fetch(`${apiBase}/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password }),
  }).then(loginHandler);
};

export const loginWithGoogle = (credential: {
  code?: string;
  credential?: string;
}): Promise<void> => {
  return fetch(`${apiBase}/auth/google`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(credential),
  }).then(loginHandler);
};

export const registerPasskey = async (silent: boolean = false) => {
  const token = getToken();
  if (!token) {
    throw new Error('Not logged in');
  }

  WebAuthnAbortService.cancelCeremony();

  const registrationOptionsResponse = await fetch(
    `${apiBase}/auth/webauthn/registration-options`,
    {
      headers: {
        authorization: token ? `Bearer ${token}` : '',
      },
    }
  ).then(async (res) => await res.json());

  const { optionsJSON, challengeToken } = registrationOptionsResponse;

  const attResp = await startRegistration({
    optionsJSON,
    useAutoRegister: silent,
  });

  const verificationResp = await fetch(
    `${apiBase}/auth/webauthn/verify-registration`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: token ? `Bearer ${token}` : '',
      },
      body: JSON.stringify({
        response: attResp,
        challengeToken,
      }),
    }
  );

  const verificationJSON = await verificationResp.json();
  if (!verificationResp.ok) {
    throw new Error(
      verificationJSON.message || 'Failed to verify passkey registration'
    );
  }

  setHasPasskeyAuth(token);
  return verificationJSON;
};

export const authenticateWithPasskey = async (autofill: boolean) => {
  WebAuthnAbortService.cancelCeremony();

  const resp = await fetch(`${apiBase}/auth/webauthn/authentication-options`, {
    method: 'GET',
  });
  const { optionsJSON, challengeToken } = await resp.json();

  const asseResp = await startAuthentication({
    optionsJSON,
    useBrowserAutofill: autofill,
  });

  const loginResponse = await fetch(
    `${apiBase}/auth/webauthn/verify-authentication`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        response: asseResp,
        challengeToken,
      }),
    }
  );

  await loginHandler(loginResponse, {
    promptForPasskeyRegistration: false,
    hasPasskeyAuth: true,
  });
};

export const impersonate = <T>(userId: string | number): Promise<void> => {
  const token = getToken();
  if (!token) {
    throw new Error('Not logged in');
  }

  return fetch(`${apiBase}/impersonate?userId=${encodeURIComponent(userId)}`, {
    headers: {
      authorization: token ? `Bearer ${token}` : '',
    },
  }).then((res) =>
    loginHandler(res, {
      promptForPasskeyRegistration: false,
    })
  );
};

export const isImpersonating = () => !!getAdminToken();

export const logout = (): void => {
  localStorage.removeItem('auth-token');
  const adminToken = getAdminToken();
  if (adminToken) {
    storeToken(adminToken);
    localStorage.removeItem('admin-token');
    window.location.href = '/admin/users';
    return;
  }

  window.location.href = '/login';
};

const forgotPasswordResHandler = async (res: Response) => {
  const responseJson = await res.json();
  if (res.status >= 400) {
    if (responseJson.message) {
      throw new Error(responseJson.message);
    }
  }
  const { errors } = responseJson;
  if (errors?.length > 0) {
    throw new Error(errors[0].message);
  }
  return;
};

export const sendForgotPasswordEmail = (email: string): Promise<void> => {
  return fetch(`${apiBase}/password-reset-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  }).then(forgotPasswordResHandler);
};

export const sendForgottenPasswordUpdate = (payload: object): Promise<void> => {
  return fetch(`${apiBase}/reset-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  }).then(forgotPasswordResHandler);
};
